import {
  useLayoutEffect,
  useRef,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type RefObject,
} from 'react'
import {
  blockBox,
  calendarHourMarks,
  clockHM,
  formatTimeRange,
  hourLabel,
  isInsideWindow,
  minutesToOffset,
  offsetToRawMinutes,
  snapToQuarter,
  CALENDAR_DAY_START_HOUR,
  CALENDAR_END_HOUR,
  CALENDAR_HOUR_PX,
  CALENDAR_START_HOUR,
  CALENDAR_WINDOW_MINUTES,
  type CalendarBlock,
  type ISODate,
} from '@app/shared'

import { PALETTE, blockMotion } from './palette'
import { Lock } from './icons'
import { anchorAtPoint, anchorOfElement, type PeekAnchor } from '../../lib/popover'
import { CAL_BLOCK_ATTR, CAL_COLUMN_ATTR, type BlockDragMode } from './useBlockDrag'

// ============================================================================
// THE TIME GRID — docs/design/calendar-brief.md → Time grid.
//
// THE WHOLE DAY, 46px an hour, scrolled to the morning — and one formula for where a block sits
// (`blockBox` in @app/shared). Nothing in this file computes a position of its own; the day
// view and the week view get the same pixels because they call the same function.
//
// IT USED TO DRAW 7 AM – 10 PM AND THAT WAS A CLAIM ABOUT THE OWNER'S DAY, not a layout. An
// early run, a late lecture and a midnight deadline all existed and none of them could be seen,
// moved, or clicked into being. The body is 24 hours tall and scrolls, opening at
// `CALENDAR_DAY_START_HOUR` so the first thing on screen is unchanged — the hours either side are
// now reachable instead of absent.
//
// THE READ-ONLY RULE IS A BRANCH, NOT A STYLE. A block with `editable: false` renders as a
// `<div>` with no handlers and no cursor affordance, and carries a provenance stamp naming whose
// calendar it really lives on — FROM CANVAS, or FROM GOOGLE since the iCal connector landed. A
// block with `editable: true` renders as a `<button>`. There is deliberately no third rendering
// that looks like a control and refuses to work (brief rule 1).
// ============================================================================

/** 24 hours at 46px. The body is exactly this tall and scrolls inside the card. */
export const GRID_HEIGHT = (CALENDAR_END_HOUR - CALENDAR_START_HOUR) * CALENDAR_HOUR_PX

/**
 * Open the grid on the morning, once, without pinning it there.
 *
 * A FRACTION OF `scrollHeight`, NOT A PIXEL CONSTANT. `GRID_HEIGHT` is a design pixel and the app
 * ships at `zoom: 1.3`; whether `scrollTop` is read in design or painted pixels is a browser
 * detail this has no business depending on, and it is the same detail that cost an hour in
 * `offsetToRawMinutes`. A share of the element's own scrollHeight is right in either unit.
 *
 * `useLayoutEffect` so the grid is already at 7 AM on the first painted frame — a body that
 * renders at midnight and then jumps is a body that looks broken for one frame every time the
 * view changes. It runs once per mount (day ↔ week remount the container), which is also the
 * only correct number of times: re-running it would yank the grid back to the morning under an
 * owner who had scrolled to 11 PM on purpose.
 */
export function useGridScroll(): RefObject<HTMLDivElement | null> {
  const ref = useRef<HTMLDivElement | null>(null)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const share = (CALENDAR_DAY_START_HOUR * 60 - CALENDAR_START_HOUR * 60) / CALENDAR_WINDOW_MINUTES
    el.scrollTop = el.scrollHeight * share
  }, [])
  return ref
}

/** The hour rules, painted into one column. */
export function HourRules() {
  return (
    <>
      {calendarHourMarks().map((hour) => (
        <div
          key={hour}
          className="pointer-events-none absolute inset-x-0 h-px bg-cal-rule"
          style={{ top: minutesToOffset(hour * 60) }}
          aria-hidden
        />
      ))}
    </>
  )
}

/**
 * The hour gutter. 52px in week view, 58px in day view — the brief's two numbers, passed in
 * rather than guessed, because the day view's labels are half a point larger and need the room.
 */
export function TimeGutter({ width, nowMinutes }: { width: number; nowMinutes: number | null }) {
  const showNow = nowMinutes !== null && isInsideWindow(nowMinutes)
  return (
    <div
      className="relative flex-none border-r border-cal-column"
      style={{ width, height: GRID_HEIGHT }}
      aria-hidden
    >
      {calendarHourMarks().map((hour) => (
        <div
          key={hour}
          className={[
            'absolute font-medium leading-none text-microcopy',
            width >= 58 ? 'right-[9px] text-[10px]' : 'right-2 text-[9.5px]',
          ].join(' ')}
          // The label sits ON the rule, lifted by half its own height.
          style={{ top: minutesToOffset(hour * 60) - 6 }}
        >
          {hourLabel(hour)}
        </div>
      ))}

      {showNow && (
        <div
          // `on-dark`, not `surface`: the pill's ground is red in BOTH themes, so its type is
          // invariant white — `surface` would turn into a dark card colour on a red chip.
          className="absolute rounded-keycap bg-cal-now px-[5px] py-[2px] text-[8.5px] font-bold leading-none text-on-dark"
          style={{ top: minutesToOffset(nowMinutes) - 8, right: 6 }}
        >
          {clockHM(nowMinutes)}
        </div>
      )}
    </div>
  )
}

/** 1.5px rule plus a 9px dot at the left edge. Drawn only on the column that is actually today. */
export function NowLine({ minutes }: { minutes: number }) {
  if (!isInsideWindow(minutes)) return null
  const top = minutesToOffset(minutes)
  return (
    <>
      <div
        className="pointer-events-none absolute inset-x-0 z-[3] h-[1.5px] bg-cal-now"
        style={{ top }}
        aria-hidden
      />
      <div
        className="pointer-events-none absolute z-[3] size-[9px] rounded-full bg-cal-now"
        style={{ top: top - 4, left: -4 }}
        aria-hidden
      />
    </>
  )
}

/**
 * One column of the grid: the rules, the wash, and whatever sits on it.
 *
 * `onPick` is the click-to-compose handler; it receives the snapped minute the click landed on AND
 * the point it landed at, so the editor can open beside the slot rather than at the top of the
 * card. A column with no `onPick` has no click behaviour at all — which is what a column in a past
 * week gets, rather than a composer that opens and then refuses to save.
 */
export function GridColumn({
  date,
  today,
  weekend,
  onPick,
  onDropAt,
  children,
  className,
}: {
  /**
   * The day this column IS. Stamped onto the element so a block drag can ask which day the
   * pointer is over without either view having to publish its own layout — see useBlockDrag.
   */
  date: ISODate
  today?: boolean
  weekend?: boolean
  onPick?: (minutes: number, anchor: PeekAnchor) => void
  onDropAt?: (minutes: number, event: React.DragEvent<HTMLDivElement>) => void
  children?: ReactNode
  className?: string
}) {
  const ref = useRef<HTMLDivElement | null>(null)
  // MEASURED, NOT ASSUMED. `box.height` is this column's rendered 24-hour day in the same units
  // `clientY` arrives in; `CALENDAR_HOUR_PX` is a design pixel, and the two stop agreeing the
  // moment the UI scale leaves 100% — which it does by default. See `offsetToRawMinutes`.
  //
  // AND THE SCROLL IS ALREADY IN IT. `box.top` comes back negative once the body is scrolled past
  // the column's own top edge, so `clientY - box.top` is the distance into the FULL day whatever
  // is on screen — no `scrollTop` term belongs here, and adding one would count the scroll twice.
  const minutesFrom = (clientY: number) => {
    const box = ref.current?.getBoundingClientRect()
    if (!box) return CALENDAR_START_HOUR * 60
    return snapToQuarter(offsetToRawMinutes(clientY - box.top, box.height))
  }

  return (
    <div
      ref={ref}
      {...{ [CAL_COLUMN_ATTR]: date }}
      className={[
        'relative min-w-0 flex-1',
        today ? 'bg-cal-today-wash' : weekend ? 'bg-cal-weekend' : '',
        className ?? '',
      ].join(' ')}
      style={{ height: GRID_HEIGHT }}
      onClick={
        onPick &&
        ((e) => {
          // Only a click on the column ITSELF composes. A click that landed on a block was a
          // click on that block, and opening a new-event form over it would be the screen
          // guessing which of the two the owner meant.
          if (e.target !== e.currentTarget) return
          // A CARET, NOT A BOX. There is no element behind a click on empty grid, so the thing
          // the editor opens beside is the pointer itself.
          onPick(minutesFrom(e.clientY), anchorAtPoint(e.clientX, e.clientY))
        })
      }
      onDragOver={
        onDropAt &&
        ((e) => {
          e.preventDefault()
          e.dataTransfer.dropEffect = 'copy'
          onDropAt(minutesFrom(e.clientY), e)
        })
      }
      onDrop={
        onDropAt &&
        ((e) => {
          e.preventDefault()
          onDropAt(minutesFrom(e.clientY), e)
        })
      }
    >
      <HourRules />
      {children}
    </div>
  )
}

// ── Blocks ──────────────────────────────────────────────────────────────────

export interface BlockChipProps {
  block: CalendarBlock
  /** 'week' is 2.5px rules and 11px titles; 'day' is 3px and 12.5px (the brief's two sizes). */
  size: 'week' | 'day'
  /**
   * ONE CALLBACK, TWO MEANINGS, DECIDED BY THE CALLER. On an editable block this opens the
   * inline editor; on a read-only one (Canvas, Google) it opens the detail peek instead — the
   * anchor is the clicked element's own rect, so the peek can be drawn beside it.
   */
  onOpen?: (block: CalendarBlock, anchor: PeekAnchor) => void
  /**
   * Starts a move or a resize. ABSENT IS A REAL ANSWER: a Canvas block, a due item and an
   * unaccepted suggestion never receive it, so they grow no handle and no grab cursor.
   */
  onDragBegin?: (
    block: CalendarBlock,
    mode: BlockDragMode,
    event: ReactPointerEvent<HTMLElement>,
  ) => void
  /** True while THIS block is the one under the pointer — it dims and the ghost speaks for it. */
  dragging?: boolean
  /** Rendered at the right of a day-view block — Keep / Dismiss on a suggestion. */
  actions?: ReactNode
  style?: CSSProperties
}

/**
 * One block on the grid.
 *
 * FOUR THINGS IT DECIDES, all of them from the model rather than from a caller:
 *   · which palette row it wears (its `source`, or the learning violet when it is a suggestion);
 *   · whether it is a button or a div (`editable`);
 *   · whether it carries FROM CANVAS or SUGGESTED FROM TASKS (`source` / `state`);
 *   · whether there is room for a time line at all (`blockBox().compact`, the brief's ~30px);
 *   · whether it can be dragged at all — which is `editable` AND `accepted`, and nothing else.
 *
 * WHY A SUGGESTION IS NOT DRAGGABLE, deliberately. An unaccepted suggestion has no row behind it
 * — its id is `suggestion-<taskId>` and it exists only in the Calendar route's memory (brief rule
 * 2). Dragging one would have to either PATCH an id no endpoint knows, or quietly write the row
 * early, which is the one thing rule 2 forbids. So the affordance is absent, exactly as it is on
 * a Canvas block, and Keep is the move that makes a suggestion movable. Its own tooltip says so.
 */
export function BlockChip({
  block,
  size,
  onOpen,
  onDragBegin,
  dragging,
  actions,
  style,
}: BlockChipProps) {
  const box = blockBox(block.startMinutes, block.endMinutes)
  const suggested = block.state === 'suggested'
  const palette = PALETTE[suggested ? 'learning' : block.source]
  const day = size === 'day'
  // PROVENANCE IS A LOOKUP, NOT A BRANCH PER SOURCE. Every block LifeOS did not author says who
  // did, in the same place and the same type size — brief rule 1, generalised the moment a
  // second read-only source arrived. A block with no stamp is a block the owner typed in.
  //
  // FROM CHAT IS THE THIRD, and it is read off `proposalId` rather than off `source`. `source` is
  // the LANE of the week (personal / work / learning / task) and putting a system name in it
  // would be a bug the calendar migration names in as many words — so "the chat suggested this
  // and I accepted it" is a link back to the proposal, and this is where that link is shown.
  const provenance =
    block.source === 'canvas'
      ? 'FROM CANVAS'
      : block.source === 'google'
        ? 'FROM GOOGLE'
        : block.proposalId
          ? 'FROM CHAT'
          : null

  // 12d's ONE PER-INSTANCE KNOB, merged into the position the block already carries. `.mo-rule`
  // is the mechanic for a radius ≤ 10 surface, and a block is `rounded-chip` (7) or
  // `rounded-tile-sm` (8) — so the hover is the lane's own straight rule arriving on the left
  // plus its tint deepening, never a lift and never a scale. See palette.ts → `blockMotion`,
  // and the MOTION SYSTEM block at the end of index.css for why radius chooses the mechanic.
  //
  // A SUGGESTION IS EXCLUDED. It is not a control (see the branch below), and 12d §2's rule for
  // anything inert is no hover, no press, no cursor change at all.
  const position: CSSProperties = {
    ...(suggested ? {} : blockMotion(palette)),
    top: box.top,
    height: box.height,
    ...(day ? { left: 10, right: 14 } : { left: 3, right: 3 }),
    ...style,
  }

  const detail = [
    formatTimeRange(block.startMinutes, block.endMinutes),
    block.location ?? undefined,
  ]
    .filter(Boolean)
    .join(' · ')

  const body = (
    <>
      <div className="flex min-w-0 flex-1 flex-col gap-[2px]">
        <div
          className={[
            'truncate font-semibold',
            palette.ink,
            // A compact block is one line inside ~12–23px, so the title drops half a step and
            // loses its leading — the alternative is a title clipped mid-x-height.
            box.compact
              ? 'text-[10px] leading-none'
              : day
                ? 'text-[12.5px] leading-[1.25]'
                : 'text-[11px] leading-[1.25]',
          ].join(' ')}
        >
          {block.title}
        </div>
        {/* Under ~30px there is no room for a second line, and a clipped time is worse than
            none — the brief says drop it and keep one line of text (Time grid). */}
        {!box.compact && (
          <div
            className={[
              'truncate font-normal',
              palette.ink2,
              day ? 'text-[10.5px] leading-[1.4]' : 'text-[9.5px] leading-[1.3]',
            ].join(' ')}
          >
            {detail}
          </div>
        )}
        {suggested && !box.compact && !day && (
          <div className={['mt-auto text-[8px] font-semibold leading-none tracking-[.05em]', palette.ink2].join(' ')}>
            SUGGESTED FROM TASKS
          </div>
        )}
        {/* PROVENANCE, ALWAYS VISIBLE ON A CANVAS BLOCK (brief rule 1). In WEEK view it goes on
            its own line under the time rather than inline to the right: a week column is ~150px
            wide, and an inline stamp takes so much of it that a real Canvas title ("COGSCI 175
            Lecture") truncates to three characters. The mockup's week blocks carry no stamp at
            all; the brief says every Canvas block carries one, and the brief wins — so it is
            kept, and given a line it fits on. */}
        {provenance && !box.compact && !day && (
          <div className={['mt-auto text-[8px] font-semibold leading-none tracking-[.05em]', palette.ink2].join(' ')}>
            {provenance}
          </div>
        )}
      </div>

      {/* At day-view size there IS room beside the title, which is where the mockup puts it —
          with the optional lock glyph, because two words plus a padlock reads as "not yours to
          move" faster than two words alone. */}
      {provenance && !box.compact && day && (
        <div className={['ml-auto flex flex-none items-center gap-[6px]', palette.ink2].join(' ')}>
          {/* THE PADLOCK MEANS “NOT YOURS TO MOVE”, so it belongs to the read-only sources only.
              A FROM CHAT block is the owner's own row — he accepted it, he can drag it, and a
              lock over something draggable would be the screen contradicting the drag handle. */}
          {!block.editable && <Lock size={12} />}
          <span className="text-[9px] font-semibold leading-none tracking-[.05em]">{provenance}</span>
        </div>
      )}
      {/* A SUGGESTION SAYS SO AT EVERY SIZE. In week view the stamp sits on its own line above;
          here it is inline beside Keep / Dismiss, because a day-view suggestion is often a
          15-minute block with no second line to put it on — and a proposal that does not admit
          to being one is the exact thing brief rule 2 forbids. */}
      {suggested && day && (
        <div className="ml-auto flex flex-none items-center gap-[7px]">
          <span className={['text-[9px] font-semibold leading-none tracking-[.05em]', palette.ink2].join(' ')}>
            SUGGESTED FROM TASKS
          </span>
          {actions}
        </div>
      )}
    </>
  )

  const shell = [
    'absolute box-border flex items-center gap-3 overflow-hidden text-left',
    day ? 'rounded-tile-sm border-l-[3px]' : 'rounded-chip border-l-[2.5px]',
    box.compact ? 'px-[8px] py-0' : day ? 'px-3 py-2' : 'px-2 py-[6px]',
    palette.tint,
    palette.rule,
  ].join(' ')

  // A READ-ONLY BLOCK IS A PLAIN DIV. No button, no cursor change, no hover lift, nothing to
  // press that then does nothing — brief rule 1, spelled as two branches rather than a `disabled`.
  // Two sources take this branch now: Canvas classes and mirrored Google events. Neither has a
  // row behind it that any endpoint in this app would accept a write for.
  //
  // A SUGGESTION TAKES THE SAME BRANCH, for the same reason and one more. Clicking one has never
  // opened anything (the route's `openBlock` returns early on `state: 'suggested'`, because
  // editing a proposal that has no row is not a thing this API can do) — so it was a control that
  // did nothing, which is precisely what rule 1 forbids. And its Keep / Dismiss are real buttons
  // living INSIDE it, which is invalid HTML: React logged "cannot be a descendant of <button>" on
  // every day-view render, and no gate in this repo can see a console. Keep and Dismiss are the
  // two things a suggestion offers; the chip around them is a label.
  if (!block.editable || !onOpen || suggested) {
    // A SUGGESTION HAS NOTHING TO PEEK AT — it has no row behind it (see the block above), so
    // its click stays absent, exactly like today. A Canvas class or a Google event DOES have a
    // click: not an editor (rule 1 forbids that), but the detail peek — title, time, location,
    // status, and whose calendar it really lives on.
    const peekable = Boolean(onOpen) && !suggested && !block.editable
    return (
      <div
        // 12d ON A READ-ONLY BLOCK THAT STILL OPENS SOMETHING. It is not an editor, but it IS a
        // click, so it gets the system's hover and press — the same treatment an editable block
        // gets below. A block that is peekable and gives no feedback is the "control that looks
        // inert" from the other direction. A block that is not peekable gets nothing at all.
        className={[
          shell,
          peekable ? 'mo-rule mo-block mo-press cursor-pointer' : '',
        ].join(' ')}
        style={position}
        {...{ [CAL_BLOCK_ATTR]: block.id }}
        role={peekable ? 'button' : undefined}
        tabIndex={peekable ? 0 : undefined}
        onClick={
          peekable
            ? (e) => {
                e.stopPropagation()
                onOpen!(block, anchorOfElement(e.currentTarget))
              }
            : undefined
        }
        onKeyDown={
          peekable
            ? (e) => {
                if (e.key !== 'Enter' && e.key !== ' ') return
                e.preventDefault()
                onOpen!(block, anchorOfElement(e.currentTarget))
              }
            : undefined
        }
        // THE TITLE IS WHERE PROVENANCE SURVIVES A COMPACT BLOCK. Under ~30px the brief says drop
        // everything but one line of text, which drops the stamp with it — so a 30-minute Google
        // event would otherwise be the one block on the grid that does not say whose it is. The
        // hover text carries it at every height, and costs no layout.
        title={
          suggested
            ? 'A proposal, not a block. Keep it and it becomes yours to move, resize and edit.'
            : provenance
              ? `${block.title} · ${provenance.toLowerCase()} · read-only`
              : undefined
        }
      >
        {body}
      </div>
    )
  }

  const movable = Boolean(onDragBegin)

  return (
    <button
      type="button"
      {...{ [CAL_BLOCK_ATTR]: block.id }}
      // `group` so the resize handle can fade in on hover; `touch-none` because a pointer drag
      // on a block IS the gesture — without it a touch drag scrolls the grid instead. The rest
      // of the grid keeps its scrolling, which is where a touch scroll actually starts.
      className={[
        shell,
        // 12d: hover is INK (the lane's rule arrives, its tint deepens), press is WEIGHT (1px
        // down, the rule holds). `.mo-block` is the one addition this screen needs — see the
        // append at the end of index.css: it is `.mo-rule`'s transition plus `opacity`, so the
        // drag dim below still travels instead of snapping.
        'group mo-rule mo-block mo-press',
        movable ? 'cursor-grab touch-none select-none active:cursor-grabbing' : '',
        // THE ORIGINAL DIMS; IT DOES NOT MOVE. Nothing has changed yet — the dashed ghost is the
        // proposal and this is still where the block is. Fading it says "you are holding this"
        // without the screen pretending the write already happened.
        dragging ? 'opacity-40 shadow-none' : '',
      ].join(' ')}
      style={position}
      onPointerDown={movable ? (e) => onDragBegin!(block, 'move', e) : undefined}
      onClick={(e) => {
        e.stopPropagation()
        onOpen(block, anchorOfElement(e.currentTarget))
      }}
      title={
        movable
          ? 'Drag to move it; drag its bottom edge to change how long it is. Click to edit.'
          : undefined
      }
    >
      {body}

      {/* THE RESIZE EDGE. A span rather than a button: it lives inside a <button>, where interactive
          content is not allowed, and it is a pointer affordance with a keyboard equivalent already
          in place — click the block and the composer's own Starts at / Ends at fields do the same
          job without a mouse. It is invisible until the block is hovered or held, because a grip
          drawn on every block at all times turns a calm grid into a row of controls. */}
      {movable && (
        <span
          role="presentation"
          onPointerDown={(e) => {
            e.stopPropagation()
            onDragBegin!(block, 'resize', e)
          }}
          onClick={(e) => e.stopPropagation()}
          className="absolute inset-x-0 bottom-0 flex cursor-ns-resize touch-none items-end justify-center"
          style={{ height: box.compact ? 5 : 8 }}
        >
          <span
            className={[
              // Retimed onto the motion system's own pair (§2) so the grip arrives with the
              // ink rather than on a reserved curve that predates the system.
              'mb-[2px] h-[2px] w-[18px] rounded-full opacity-0 transition-opacity duration-[var(--dur-hover)] ease-[var(--ease-ink)] group-hover:opacity-60',
              dragging ? 'opacity-90' : '',
              palette.dot,
            ].join(' ')}
            aria-hidden
          />
        </span>
      )}
    </button>
  )
}

/**
 * The dashed drop ghost, with the time it is proposing.
 *
 * It exists so a drag says what it is about to do BEFORE it does it — the brief asks for the
 * proposed time on the ghost, and a drop that silently lands somewhere is the interaction most
 * likely to put an hour in the wrong place.
 */
export function GhostSlot({
  label,
  startMinutes,
  endMinutes,
  size,
}: {
  label: string
  startMinutes: number
  endMinutes: number
  size: 'week' | 'day'
}) {
  const box = blockBox(startMinutes, endMinutes)
  const day = size === 'day'
  return (
    <div
      className={[
        'pointer-events-none absolute z-[2] box-border flex overflow-hidden',
        // THE GHOST WEARS THE SHAPE OF THE THING IT IS PROPOSING. A day-view block puts its time
        // beside the title, and a week-view block puts it underneath — because a week column is
        // ~130px wide and a row that reserves space for "2:00 – 3:30 PM" leaves the title nothing
        // to truncate into. A ghost that shows a time and no name is a proposal that will not say
        // what it is about to move.
        day ? 'items-center gap-[10px] px-3 py-2' : 'flex-col justify-center gap-[2px] px-2 py-[6px]',
        'rounded-tile-sm border-[1.5px] border-dashed border-cal-ghost-line bg-cal-ghost-fill',
      ].join(' ')}
      style={{
        top: box.top,
        height: box.height,
        ...(day ? { left: 10, right: 14 } : { left: 3, right: 3 }),
      }}
      aria-hidden
    >
      <span
        className={[
          'min-w-0 truncate font-semibold text-cal-ghost-ink',
          day ? 'text-[11.5px] leading-[1.3]' : 'text-[11px] leading-[1.25]',
        ].join(' ')}
      >
        {label}
      </span>
      {/* Under ~30px there is one line of room, and the title is the half that identifies the
          block — the same call `BlockChip` makes at the same height. */}
      {(!box.compact || day) && (
        <span
          className={[
            'flex-none truncate font-normal text-microcopy',
            day ? 'ml-auto text-[10px] leading-none' : 'text-[9.5px] leading-[1.3]',
          ].join(' ')}
        >
          {formatTimeRange(startMinutes, endMinutes)}
        </span>
      )}
    </div>
  )
}
